import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAdminRole } from '../../../hooks/useAdminRole';

interface AuditLog {
  id: string;
  created_at: string;
  user_id: string | null;
  action: string;
  resource: string;
  resource_id: string | null;
  details: string | null;
  ip_address: string | null;
  profile?: { full_name: string; email: string } | null;
}

const ACTION_COLORS: Record<string, string> = {
  create:  'bg-emerald-500/20 text-emerald-400',
  update:  'bg-cyan-500/20    text-cyan-400',
  delete:  'bg-red-500/20     text-red-400',
  login:   'bg-violet-500/20  text-violet-400',
  logout:  'bg-white/10       text-white/50',
  block:   'bg-orange-500/20  text-orange-400',
  unblock: 'bg-emerald-500/20 text-emerald-400',
  view:    'bg-white/10       text-white/40',
};

const ACTION_LABEL: Record<string, string> = {
  create:  'Criação',
  update:  'Edição',
  delete:  'Exclusão',
  login:   'Login',
  logout:  'Logout',
  block:   'Bloqueio',
  unblock: 'Desbloqueio',
  view:    'Visualização',
};

const RESOURCE_ICON: Record<string, string> = {
  user:         'ri-user-line',
  plan:         'ri-price-tag-3-line',
  subscription: 'ri-file-list-3-line',
  payment:      'ri-bank-card-line',
  gateway:      'ri-secure-payment-line',
  settings:     'ri-settings-3-line',
  admin:        'ri-shield-user-line',
};

const AdminAuditoriaPage = () => {
  const { isMasterAdmin, isAdmin } = useAdminRole();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterResource, setFilterResource] = useState('all');
  const [selected, setSelected] = useState<AuditLog | null>(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('audit_logs')
      .select('id, created_at, user_id, action, resource, resource_id, details, ip_address')
      .order('created_at', { ascending: false })
      .limit(500);

    const rows = (data as any[]) || [];

    const userIds = [...new Set(rows.map((l: any) => l.user_id).filter(Boolean))];
    let profileMap: Record<string, any> = {};
    if (userIds.length > 0) {
      const { data: profs } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', userIds);
      (profs || []).forEach((p: any) => { profileMap[p.id] = p; });
    }

    setLogs(
      rows.map((l: any) => ({
        ...l,
        profile: l.user_id ? profileMap[l.user_id] || null : null,
      }))
    );
    setLoading(false);
  }, []);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const fmt = (d: string) =>
    new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const filtered = logs.filter((l) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      l.action?.toLowerCase().includes(q) ||
      l.resource?.toLowerCase().includes(q) ||
      l.profile?.full_name?.toLowerCase().includes(q) ||
      l.profile?.email?.toLowerCase().includes(q);
    const matchAction   = filterAction   === 'all' || l.action   === filterAction;
    const matchResource = filterResource === 'all' || l.resource === filterResource;
    return matchSearch && matchAction && matchResource;
  });

  const actions   = [...new Set(logs.map((l) => l.action))].filter(Boolean);
  const resources = [...new Set(logs.map((l) => l.resource))].filter(Boolean);

  if (!isMasterAdmin && !isAdmin) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <i className="ri-lock-line text-4xl text-white/20 mb-3" />
          <p className="text-white/40 text-[14px]">Acesso restrito a Master Admin e Admin</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-[24px] font-700 text-white">Auditoria</h1>
          <p className="text-white/40 text-[14px] mt-1">Registro de ações administrativas da plataforma</p>
        </div>
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2">
          <i className="ri-shield-check-line text-violet-400 text-[15px]" />
          <span className="text-[12px] text-white/50">{filtered.length} registros</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2 flex-1 min-w-[200px]">
          <i className="ri-search-line text-white/40 text-[16px]" />
          <input
            className="bg-transparent text-white text-[14px] outline-none placeholder:text-white/30 w-full"
            placeholder="Buscar por ação, recurso, usuário..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white/70 text-[13px] outline-none cursor-pointer"
        >
          <option value="all" className="bg-[#1A1A2E]">Todas as ações</option>
          {actions.map((a) => (
            <option key={a} value={a} className="bg-[#1A1A2E]">{ACTION_LABEL[a] || a}</option>
          ))}
        </select>
        <select
          value={filterResource}
          onChange={(e) => setFilterResource(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white/70 text-[13px] outline-none cursor-pointer"
        >
          <option value="all" className="bg-[#1A1A2E]">Todos os recursos</option>
          {resources.map((r) => (
            <option key={r} value={r} className="bg-[#1A1A2E]">{r}</option>
          ))}
        </select>
        <button
          onClick={loadLogs}
          className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-white/50 hover:text-white text-[13px] transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line" />
          Atualizar
        </button>
      </div>

      <div className="flex gap-5">
        {/* Table */}
        <div className="flex-1 bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line text-2xl text-violet-400 animate-spin" />
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-5 py-3 text-[11px] font-600 text-white/40 uppercase tracking-wider">Data/Hora</th>
                  <th className="text-left px-5 py-3 text-[11px] font-600 text-white/40 uppercase tracking-wider">Usuário</th>
                  <th className="text-left px-5 py-3 text-[11px] font-600 text-white/40 uppercase tracking-wider">Ação</th>
                  <th className="text-left px-5 py-3 text-[11px] font-600 text-white/40 uppercase tracking-wider">Recurso</th>
                  <th className="text-left px-5 py-3 text-[11px] font-600 text-white/40 uppercase tracking-wider">IP</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((log) => (
                  <tr
                    key={log.id}
                    onClick={() => setSelected(log.id === selected?.id ? null : log)}
                    className={`border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors ${selected?.id === log.id ? 'bg-violet-600/10' : ''}`}
                  >
                    <td className="px-5 py-3.5 text-[12px] text-white/60 whitespace-nowrap">{fmt(log.created_at)}</td>
                    <td className="px-5 py-3.5">
                      <div>
                        <p className="text-[13px] text-white font-500">{log.profile?.full_name || 'Sistema'}</p>
                        <p className="text-[11px] text-white/40">{log.profile?.email || '—'}</p>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`text-[11px] font-600 px-2 py-0.5 rounded-full whitespace-nowrap ${ACTION_COLORS[log.action] || 'bg-white/10 text-white/40'}`}>
                        {ACTION_LABEL[log.action] || log.action}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1.5">
                        <i className={`${RESOURCE_ICON[log.resource] || 'ri-file-line'} text-white/40 text-sm`} />
                        <span className="text-[12px] text-white/60">{log.resource}</span>
                        {log.resource_id && (
                          <span className="text-[10px] text-white/25 font-500">#{log.resource_id.slice(0, 6)}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-[12px] text-white/40">{log.ip_address || '—'}</td>
                    <td className="px-5 py-3.5">
                      <i className="ri-arrow-right-s-line text-white/30 text-lg" />
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <i className="ri-file-search-line text-3xl text-white/20" />
                        <p className="text-white/30 text-[13px]">
                          {search || filterAction !== 'all' || filterResource !== 'all'
                            ? 'Nenhum registro encontrado com os filtros aplicados'
                            : 'Nenhum registro de auditoria ainda. As ações administrativas aparecerão aqui.'}
                        </p>
                        {search || filterAction !== 'all' || filterResource !== 'all' ? (
                          <button
                            onClick={() => { setSearch(''); setFilterAction('all'); setFilterResource('all'); }}
                            className="text-[12px] text-violet-400 hover:text-violet-300 cursor-pointer"
                          >
                            Limpar filtros
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="w-[280px] bg-white/5 border border-white/10 rounded-2xl p-5 shrink-0">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[15px] font-600 text-white">Detalhe</h3>
              <button onClick={() => setSelected(null)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/10 text-white/50 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="space-y-3.5">
              <div>
                <p className="text-[10px] text-white/30 uppercase tracking-wider font-600 mb-1">Ação</p>
                <span className={`text-[12px] font-600 px-2 py-0.5 rounded-full ${ACTION_COLORS[selected.action] || 'bg-white/10 text-white/40'}`}>
                  {ACTION_LABEL[selected.action] || selected.action}
                </span>
              </div>
              <div>
                <p className="text-[10px] text-white/30 uppercase tracking-wider font-600 mb-1">Recurso</p>
                <p className="text-[13px] text-white">{selected.resource} {selected.resource_id && <span className="text-white/30 text-[11px]">({selected.resource_id})</span>}</p>
              </div>
              <div>
                <p className="text-[10px] text-white/30 uppercase tracking-wider font-600 mb-1">Usuário</p>
                <p className="text-[13px] text-white">{selected.profile?.full_name || 'Sistema'}</p>
                <p className="text-[11px] text-white/40">{selected.profile?.email || '—'}</p>
              </div>
              <div>
                <p className="text-[10px] text-white/30 uppercase tracking-wider font-600 mb-1">Data/Hora</p>
                <p className="text-[12px] text-white/70">{fmt(selected.created_at)}</p>
              </div>
              {selected.ip_address && (
                <div>
                  <p className="text-[10px] text-white/30 uppercase tracking-wider font-600 mb-1">IP</p>
                  <p className="text-[12px] text-white/70">{selected.ip_address}</p>
                </div>
              )}
              {selected.details && (
                <div>
                  <p className="text-[10px] text-white/30 uppercase tracking-wider font-600 mb-1">Detalhes</p>
                  <pre className="text-[11px] text-white/60 bg-white/5 rounded-lg p-3 overflow-auto max-h-40 whitespace-pre-wrap">{
                    (() => {
                      try { return JSON.stringify(JSON.parse(selected.details || '{}'), null, 2); }
                      catch { return selected.details; }
                    })()
                  }</pre>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAuditoriaPage;
